import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import UIIcon, { UIIconType } from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { getContentfulUrl } from '@/utils/contentful'

export type RunnerGoalBubbleProps = {
  goal: string
  exiting: boolean
}

const backgrounds = [
  {
    key: 'bubble_1',
    background: 'bubble1Background',
  },
  {
    key: 'bubble_2',
    background: 'bubble2Background',
  },
  {
    key: 'bubble_3',
    background: 'bubble3Background',
  },
]

const RunnerGoalBubble = ({ goal, exiting }: RunnerGoalBubbleProps) => {
  const { gsap } = useGSAP()

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/runner/${goal}_content.png`, { w: 512, fm: betterFormat })
    setImageUrl(url)
  }, [goal, betterFormat])

  const background = backgrounds.find((item) => item.key === goal)?.background as UIIconType

  const elementRef = useRef<HTMLDivElement>(null)
  const arrowRef = useRef<SVGSVGElement>(null)
  const arrowBodyRef = useRef<SVGPathElement>(null)
  const arrowHeadRef = useRef<SVGPathElement>(null)
  const arrowBodyAltRef = useRef<SVGPathElement>(null)
  const arrowHeadAltRef = useRef<SVGPathElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (goal) {
        if (!exiting) {
          gsap
            .timeline({ delay: 0 })
            .fromTo(
              elementRef.current,
              {
                opacity: 0,
              },
              {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.out',
              },
              0
            )
            .addLabel('arrow', 1.2)
            .fromTo(
              [arrowBodyAltRef.current, arrowBodyRef.current],
              { drawSVG: '0% 0%' },
              { drawSVG: '0% 100%', duration: 1, ease: 'power2.out', stagger: 0.15 },
              'arrow'
            )
            .fromTo(
              [arrowHeadAltRef.current, arrowHeadRef.current],
              { drawSVG: '50% 50%' },
              { drawSVG: '0% 100%', duration: 0.3, ease: 'power2.out', stagger: 0.15 },
              'arrow+=0.7'
            )
        } else {
          gsap.timeline().to(arrowRef.current, { opacity: 0, duration: 0.5, ease: 'power2.out' }, 0)
        }
      }
    })

    return () => ctx.revert()
  }, [goal, exiting, elementRef, arrowBodyRef, arrowHeadRef, gsap])

  return (
    <div ref={elementRef} className='RunnerGoalBubble w-200 h-200 lg:w-256 lg:h-256 relative -top-24 lg:-top-32'>
      <UIIcon
        icon={background}
        className={`absolute top-0 left-0 w-full h-full transition-colors duration-500 ease-out-quad ${exiting ? 'text-light-blue' : 'text-tertiary'}`}
      />
      <img
        className={`relative transition-opacity duration-500 ease-out-quad ${exiting ? 'opacity-0' : ''}`}
        src={imageUrl}
        alt={goal}
      />
      <div
        className={`absolute top-0 left-0 w-full h-full flex items-center justify-center transition-opacity duration-500 ease-out-quad ${exiting ? '' : 'opacity-0'}`}
      >
        <UIIcon icon='checked' className='text-tertiary text-64 lg:text-80' />
      </div>
      <svg
        ref={arrowRef}
        className='absolute top-16 lg:top-32 -left-16 w-64 h-64 lg:w-80 lg:h-80 -scale-y-100 text-primary'
        viewBox='0 0 80 80'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          ref={arrowBodyAltRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowHeadAltRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowBodyRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
        />
        <path
          ref={arrowHeadRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
        />
      </svg>
    </div>
  )
}
export default RunnerGoalBubble
